import {
    Button, Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import React, {useEffect, useState} from "react";
import './InboxView.scss';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ForwardIcon from '@mui/icons-material/Forward';
import ReplyIcon from '@mui/icons-material/Reply';
import {ePismoClientInstance} from "../Service/EPismoClient";
import {useKeycloak} from "@react-keycloak/web";
import InboxConfirming from "./InboxConfirming";
import {useNavigate} from "react-router-dom";

interface InboxDocument {
    id: string;
    documentTypeName: string;
    title: string;
    documentNumber: string;
    sendDate: string;
    senderName: string;
    senderAddress: string;
    senderId?: string;
    receiveDate: boolean;
    attachmentName: string;
}

interface InboxDocumentResponse {
    count: number;
    messages: InboxDocument[];
}

export default function InboxView() {

    const [data, setData] = React.useState<InboxDocumentResponse>({count: 0, messages: []});
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState<InboxDocument>({} as InboxDocument);
    const [query, setQuery] = useState("")
    const [offset, setOffset] = useState(0)
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate();

    const keycloak = useKeycloak();

    function load() {
        setLoading(true)
        let params = new URLSearchParams();
        if (query)
            params.set("query", query)
        if (offset)
            params.set("offset", offset.toString())
        ePismoClientInstance.get("/v1/gui/inbox?" + params.toString(), keycloak).then(r => {
            setData(r.data)
            setLoading(false)
        })
    }

    useEffect(() => {
        load();
    }, []);

    function openDocument(message: InboxDocument) {
        setMessage(message);
        if (message.receiveDate) {
            openDocumentForce(message);
        } else {
            setOpen(true);
        }
    }

    function openDocumentForce(message: InboxDocument) {
        ePismoClientInstance.getFile("/v1/gui/inbox/download/" + message.id, keycloak)
            .then(response => {
                const href = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', message.documentNumber + '.pdf'); //or any other extension
                document.body.appendChild(link);
                link.click();
// clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                load();
                setOpen(false);
            });
    }

    function openAttachment(message: InboxDocument) {
        ePismoClientInstance.getFile("/v1/gui/inbox/attachment/" + message.id, keycloak)
            .then(response => {
                const href = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', message.attachmentName); //or any other extension
                document.body.appendChild(link);
                link.click();
// clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                load();
                setOpen(false);
            });
    }

    useEffect(() => {
        load();
    }, [offset]);

    return (
        <div className="inboxList">
            <Grid container spacing={3}>
                <Grid xs={12}>
                    <Typography align="left" variant="h1">Skrzynka odbiorcza</Typography>
                </Grid>
                <Grid xs={12}>
                    <div style={{textAlign: "left"}}>
                        <TextField onChange={e => setQuery(e.target.value)} label="Szukaj" size="small" sx={{mr: 2}}
                                   disabled={loading}/>
                        <Button variant="contained" onClick={load}>
                            {!loading && <><span style={{marginRight: 5}}>Szukaj</span> <SearchIcon/></>}
                            {loading && <>Trwa ładowanie...</>}
                        </Button>
                    </div>
                </Grid>
                <Grid xs={12}>
                    <Grid xs={12}>
                        <Table>
                            <TableHead>
                                <TableCell width="30%">Rodzaj dokumentu / Nr sygnatury</TableCell>
                                <TableCell width="12%">Data podpisania</TableCell>
                                <TableCell width="12%">Data odbioru</TableCell>
                                <TableCell width="20%">Nadawca</TableCell>
                                <TableCell width="26%">Akcje</TableCell>
                            </TableHead>
                            <TableBody>
                                {data.messages.map((message) => (
                                    <TableRow key={message.id} className={message.receiveDate ? "" : "unread"}>
                                        <TableCell>
                                            <span
                                                className="link">{message.documentTypeName}</span>
                                            <span>{message.title}</span> {message.documentNumber}
                                        </TableCell>
                                        <TableCell>{message.sendDate}</TableCell>
                                        <TableCell>{message.receiveDate ? message.receiveDate : "-"}</TableCell>
                                        <TableCell>
                                            <span>{message.senderName}</span> <span>{message.senderAddress}</span>
                                        </TableCell>
                                        <TableCell>
                                            {!message.receiveDate ? (
                                                <Button variant="contained" size="small"
                                                        sx={{
                                                            p: 0,
                                                            mr: 0.5,
                                                            py: 0.5,
                                                            px: 1,
                                                            minWidth: 'auto',
                                                            fontSize: '0.7rem'
                                                        }}
                                                        onClick={e => openDocument(message)}>Otwórz dokument</Button>
                                            ) : (() => {
                                                const actions = [
                                                    <Tooltip title="Otwórz dokument">
                                                        <Button variant="contained" size="small" fullWidth
                                                                sx={{minWidth: 'auto'}}
                                                                onClick={e => openDocument(message)}>
                                                            <FileDownloadIcon fontSize="small"/>
                                                        </Button>
                                                    </Tooltip>,
                                                    ...(message.attachmentName ? [
                                                        <Tooltip title="Pobierz załączniki">
                                                            <Button variant="contained" size="small" fullWidth
                                                                    sx={{minWidth: 'auto'}}
                                                                    onClick={e => openAttachment(message)}>
                                                                <AttachFileIcon fontSize="small"/>
                                                            </Button>
                                                        </Tooltip>
                                                    ] : []),
                                                    <Tooltip title="Odpowiedz">
                                                        <Button variant="contained" size="small" fullWidth
                                                                sx={{minWidth: 'auto'}}
                                                                onClick={() => navigate('/nadaj-pismo', {state: {replyTo: message}})}>
                                                            <ReplyIcon fontSize="small"/>
                                                        </Button>
                                                    </Tooltip>,
                                                    <Tooltip title="Przekaż">
                                                        <Button variant="contained" size="small" fullWidth
                                                                sx={{minWidth: 'auto'}}
                                                                onClick={() => navigate('/nadaj-pismo', {state: {forwardFrom: message}})}>
                                                            <ForwardIcon fontSize="small"/>
                                                        </Button>
                                                    </Tooltip>,
                                                ];
                                                return (
                                                    <Grid container columns={4} spacing={0}>
                                                        {actions.map((action, index) => (
                                                            <Grid key={index} xs={1} sx={{px: 0.5}}>{action}</Grid>
                                                        ))}
                                                    </Grid>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <Pagination count={Math.floor(data.count / 10) + 1}
                                    onChange={(e, n) => setOffset((n - 1) * 10)}/>
                    </Grid>
                </Grid>
            </Grid>
            <Dialog open={open} className="InboxCofirmingDialog">
                <DialogTitle sx={{textAlign: "center"}}>Potwierdzenie odbioru</DialogTitle>
                <DialogContent sx={{textAlign: "center"}}>
                    <p>Po otwarciu wiadomości, będzie ona przechowywana przez 3 dni, po czym zostanie usunięta. W
                        systemie pozostanie jedynie sygnatura dokumentu do weryfikacji autentyczności.</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={e => openDocumentForce(message)} color="primary">
                        Pobierz dokument
                    </Button>
                    <Button onClick={() => setOpen(false)} color="secondary">
                        Nie chcę otwierać dokumentu.
                    </Button>
                </DialogActions>
            </Dialog>
            <InboxConfirming force={true} onDeclineAdditionalAction={() => window.location.href = '/'}/>
        </div>
    )
}